import type { ApiClient } from './client';

/**
 * The organization's public face and its verification, as
 * `veri-plus-backend` serves them.
 *
 * `routes/v1/organization.routes.ts`, mounted at `/v1/organization`:
 * `GET /` answers `{ organization }` to any org user; `GET /readiness`
 * answers `organizationService.readiness` to any org user; `PATCH /profile`
 * with `updateManufacturerProfileSchema`, at least one field, merged into the
 * stored profile, answers `{ organization }`; `POST /verification-request`
 * with an optional `note` up to 500 characters answers `{ organization }`,
 * 201 when it queued and 200 on a replay while pending, or 409
 * `already_verified` or `not_ready_for_verification`. Both writes are
 * `requireOrgAdmin`, so a member gets 403 `insufficient_role`. D17.
 */

export interface ManufacturerProfile {
  legalName?: string;
  registrationNumber?: string;
  country?: string;
  supportEmail?: string;
  supportPhone?: string;
  website?: string;
}

/** `organization.model.ts` through its `toJSON`: the fields this screen reads. */
export interface Organization {
  id: string;
  name: string;
  manufacturerProfile?: ManufacturerProfile;
  verifiedAt?: string;
  verificationRequestedAt?: string;
  verificationRequestNote?: string;
  /** Set by staff only, Q5: the org registers other brands' QR codes as demo samples. D36. */
  demoMode?: boolean;
  /** The accounts that get the demo behaviour when they scan, set by staff. */
  demoScannerEmails?: string[];
}

export interface Issue {
  field: string;
  message: string;
}

export interface Readiness {
  ready: boolean;
  pilotMode: boolean;
  verified: boolean;
  verificationRequested: boolean;
  /** What stands between the org and being able to ask, a superset of `organizationIssues`. */
  verificationIssues: Issue[];
  organizationIssues: Issue[];
  products: { productId: string; name: string; regulated: boolean; issues: Issue[] }[];
}

export const organizationPath = '/v1/organization';
export const readinessPath = '/v1/organization/readiness';

export async function getOrganization(api: ApiClient, signal?: AbortSignal): Promise<Organization> {
  const answer = await api.get<{ organization: Organization }>(
    organizationPath,
    signal ? { signal } : {},
  );
  return answer.organization;
}

export function getReadiness(api: ApiClient, signal?: AbortSignal): Promise<Readiness> {
  return api.get<Readiness>(readinessPath, signal ? { signal } : {});
}

export async function updateProfile(
  api: ApiClient,
  fields: ManufacturerProfile,
): Promise<Organization> {
  const answer = await api.patch<{ organization: Organization }>(
    `${organizationPath}/profile`,
    fields,
  );
  return answer.organization;
}

export async function requestVerification(api: ApiClient, note: string): Promise<Organization> {
  const answer = await api.post<{ organization: Organization }>(
    `${organizationPath}/verification-request`,
    note === '' ? {} : { note },
  );
  return answer.organization;
}

export const profileFields = [
  'legalName',
  'registrationNumber',
  'country',
  'supportEmail',
  'supportPhone',
  'website',
] as const;

export type ProfileField = (typeof profileFields)[number];

/**
 * What Save sends: each field whose trimmed value differs from the stored
 * one, and nothing else, since the API merges. A cleared field is sent as
 * `''`, and the API decides whether it may be blank. D17.
 */
export function changedFields(
  stored: ManufacturerProfile | undefined,
  draft: Record<ProfileField, string>,
) {
  const changed: ManufacturerProfile = {};
  for (const field of profileFields) {
    const next = draft[field].trim();
    if (next !== (stored?.[field] ?? '')) changed[field] = next;
  }
  return changed;
}

/** Rule 7 on copy the API writes: its em dashes read as a colon. */
export const plain = (text: string) => text.replace(/\s*\u2014\s*/g, ': ');

export type Check =
  | { key: string; title: string; met: true; value: string }
  | { key: string; title: string; met: false; detail: string; fix?: 'products' | 'code-batches' };

/**
 * The board's "Before you can issue codes" rows. The three profile rows are
 * always drawn, green with the stored value or amber with the API's message.
 * Photos and regulated batch details are drawn only while they stand, by the
 * user's choice. D17.
 */
export function checklist(readiness: Readiness, profile: ManufacturerProfile | undefined): Check[] {
  const issue = (field: string) => readiness.organizationIssues.find((i) => i.field === field);
  const row = (key: string, title: string, field: string, value: string | undefined): Check => {
    const open = issue(field);
    return open
      ? { key, title, met: false, detail: plain(open.message) }
      : { key, title, met: true, value: value ?? '' };
  };
  const rows: Check[] = [
    row('legalName', 'Registered legal name', 'manufacturerProfile.legalName', profile?.legalName),
    row('country', 'Country of manufacture', 'manufacturerProfile.country', profile?.country),
    row(
      'contact',
      'Support contact',
      'manufacturerProfile.supportEmail',
      profile?.supportEmail || profile?.supportPhone,
    ),
  ];
  const noPhoto = readiness.products.filter((p) =>
    p.issues.some((i) => i.field === 'imageUrl'),
  ).length;
  if (noPhoto > 0) {
    rows.push({
      key: 'photos',
      title: 'Product photos',
      met: false,
      detail: `${noPhoto} of ${readiness.products.length} products ${noPhoto === 1 ? 'has' : 'have'} no photo: a consumer cannot compare the pack without one.`,
      fix: 'products',
    });
  }
  const regulated = readiness.products.filter((p) => p.regulated).map((p) => p.name);
  if (regulated.length > 0) {
    rows.push({
      key: 'provenance',
      title: 'Batch details for regulated products',
      met: false,
      detail: `${listed(regulated)} ${regulated.length === 1 ? 'needs' : 'need'} a batch number, manufacture date and expiry on every batch.`,
      fix: 'code-batches',
    });
  }
  return rows;
}

/** The "2 to go" chip and the sidebar badge: the amber rows. */
export const toGo = (readiness: Readiness) =>
  checklist(readiness, undefined).filter((c) => !c.met).length;

/** `A`, `A and B`, `A, B and C`. */
function listed(names: string[]): string {
  if (names.length === 1) return names[0]!;
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/**
 * The verification panel's drawn states, less "withdrawn": a staff revoke
 * unsets `verifiedAt` and leaves no mark the API serves, so a withdrawn org
 * reads as never verified. DEFERRED 4.13, D17.
 */
export type Verification =
  | { state: 'verified' }
  | { state: 'requested'; at: string }
  | { state: 'blocked'; gaps: string[] }
  | { state: 'can_ask' };

/** The board's own sentence for the one gap that blocks asking but not issuing. */
export const registrationGap =
  'Add your company or licence number first: it is what staff look up. Not needed to issue codes.';

export function verificationOf(organization: Organization, readiness: Readiness): Verification {
  if (organization.verifiedAt !== undefined) return { state: 'verified' };
  if (organization.verificationRequestedAt !== undefined) {
    return { state: 'requested', at: organization.verificationRequestedAt };
  }
  if (readiness.verificationIssues.length > 0) {
    return {
      state: 'blocked',
      gaps: readiness.verificationIssues.map((i) =>
        i.field === 'manufacturerProfile.registrationNumber' ? registrationGap : plain(i.message),
      ),
    };
  }
  return { state: 'can_ask' };
}
